import { useEffect, useRef, useState } from "react";
import { Button } from 'react-bootstrap';

type Props = {
  height?: number;
  onChangeDataUrl: (dataUrl: string | null) => void;
};

export default function SignaturePad({ height = 200, onChangeDataUrl }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  function resetCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    const ratio = window.devicePixelRatio || 1;
    const width = parent.clientWidth - 10;

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Paint white background
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, width, height);

    // Stroke defaults
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "black";

    setHasInk(false);
    last.current = null;
    onChangeDataUrl(null);
  }

  useEffect(() => {
    resetCanvas();
    const onResize = () => resetCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getPos(e: PointerEvent) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawing.current = true;
    canvas.setPointerCapture(e.pointerId);
    last.current = getPos(e.nativeEvent);
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const p = getPos(e.nativeEvent);
    const prev = last.current;
    if (!prev) {
      last.current = p;
      return;
    }

    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();

    last.current = p;

    if (!hasInk) setHasInk(true);
  }

  function up() {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;

    const canvas = canvasRef.current;
    if (!canvas) return;

    if (hasInk) onChangeDataUrl(canvas.toDataURL("image/png"));
  }

  function clear() {
    resetCanvas();
  }

  return (
    <div>
      <div className="border rounded bg-body" style={{ width: "100%", touchAction: "none" }}>
        <canvas
          ref={canvasRef}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
        />
      </div>
      <div className="text-center mt-2">
        <Button variant="outline-secondary" size="sm" onClick={clear}>
          Clear
        </Button>
      </div>
    </div>
  );
}